import { gql, useQuery } from '@apollo/client';
import { Typography } from 'hephaestus/web';
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import Lottie from 'react-lottie';

const FETCH_FILE_DOCUMENT_URL = gql`
  query fetchFileDocumentDownloadUrl($fileDocumentId: ObjectID!) {
    fetchFileDocument(fileDocumentId: $fileDocumentId) {
      fileDocument {
        downloadUrl
        fileName
        mediaType
      }
    }
  }
`;

type InlineImageProps = {
  fileDocumentId: string;
  style?: CSSProperties;
  /** Shown until the image is downloaded and painted. Defaults to `...`. */
  placeholder?: ReactNode;
};

const defaultOptions = {
  loop: true,
  autoplay: true,
};

export const InlineImage = ({
  fileDocumentId,
  style,
  placeholder,
}: InlineImageProps) => {
  const [animationData, setAnimationData] = useState<any>(undefined);
  const [imageSettled, setImageSettled] = useState(false);
  const [renderedImageUrl, setRenderedImageUrl] = useState<string | undefined>(
    undefined,
  );
  const imageRef = useRef<HTMLImageElement | null>(null);
  const { data, loading } = useQuery(FETCH_FILE_DOCUMENT_URL, {
    variables: { fileDocumentId, skipErrorHandler: true },
    fetchPolicy: 'no-cache',
  });

  const fetchAnimationData = async () => {
    try {
      const response = await fetch(
        data?.fetchFileDocument?.fileDocument?.downloadUrl,
      );
      if (!response.ok) {
        throw new Error('Failed to fetch animation data');
      }
      const jsonData = await response.json();
      if (jsonData) setAnimationData(jsonData);
    } catch (error) {
      console.error('Error fetching file:', error);
    }
  };

  useEffect(() => {
    if (
      data?.fetchFileDocument?.fileDocument?.fileName.split('.')[1] === 'json'
    ) {
      void fetchAnimationData();
    }
  }, [data]);

  const isJsonFile =
    data?.fetchFileDocument?.fileDocument?.fileName.split('.')[1] === 'json';
  const imageUrl = isJsonFile
    ? undefined
    : data?.fetchFileDocument?.fileDocument?.downloadUrl;

  useEffect(() => {
    if (imageUrl !== renderedImageUrl) {
      setRenderedImageUrl(imageUrl);
      setImageSettled(false);
    }
  }, [imageUrl, renderedImageUrl]);

  // A cached image can finish loading before its onLoad handler is attached,
  // so `complete` is the only signal left once the event has been missed.
  useEffect(() => {
    if (imageUrl && imageRef.current?.complete) setImageSettled(true);
  }, [imageUrl]);

  return (
    <>
      {(loading || (imageUrl && !imageSettled)) &&
        (placeholder ?? <Typography variant='bodyLarge'>...</Typography>)}
      {imageUrl && (
        <img
          ref={imageRef}
          src={imageUrl}
          alt={data?.fetchFileDocument?.fileDocument?.fileName}
          onLoad={() => setImageSettled(true)}
          onError={() => setImageSettled(true)}
          style={{
            ...(style ?? { borderTop: '1px solid grey' }),
            ...(imageSettled ? undefined : { display: 'none' }),
          }}
        />
      )}
      {isJsonFile && animationData && (
        <Lottie
          options={{ ...defaultOptions, animationData }}
          height={style?.height}
          width='100%'
        />
      )}
    </>
  );
};
